import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { Provider as JotaiProvider } from "jotai";
import { isEqual } from "lodash-es";
import { PostHogProvider } from "posthog-js/react";
import { apiAxiosInstance } from "~/api";
import { AuthClient } from "~/api/AuthClient";
import { AuthProvider } from "~/components/AuthContext";
import { FallbackEmpty } from "~/components/FallbackEmpty";
import { FallbackError } from "~/components/FallbackError";
import { BuildUpdateNotice } from "~/components/app-update/BuildUpdateNotice";
import { Toaster } from "~/components/ui/Toaster";
import { createPostHogConfig } from "~/utils/postHogConfig";
import { subscribeAuthRouter } from "~/utils/subscribeAuthRouter";
import i18n from "./i18n";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

window.__APP_VERSION__ = __APP_VERSION__;
window.__BUILD_DATE__ = __BUILD_DATE__;

console.debug(
  `Running:\napp version : ${window.__APP_VERSION__}\nbuild time  : ${window.__BUILD_DATE__}`,
);

const dsn = import.meta.env.VITE_SENTRY_DSN;
let sentryRootElementOptions: ReactDOM.RootOptions = {};
if (dsn) {
  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: `di-platform-frontend@${window.__APP_VERSION__}`,
    denyUrls: [/https?:\/\/di\.local/, /https?:\/\/localhost/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],
  });

  sentryRootElementOptions = {
    // Callback called when an error is thrown and not caught by an Error Boundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.error("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an Error Boundary.
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Caught boundary error", error, errorInfo.componentStack);
    }),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.info(
        "React recovered from error",
        error,
        errorInfo.componentStack,
      );
    }),
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // https://tanstack.com/query/latest/docs/framework/react/guides/suspense#using-usequerypromise-and-reactuse-experimental
      // TODO: Do we want to do lazy loading this way?
      experimental_prefetchInRender: true,
    },
  },
});

if (import.meta.env.MODE === "development") {
  // Expose queryClient for Chrome extension React Query Devtools
  // https://chromewebstore.google.com/detail/tanstack-query-devtools/annajfchloimdhceglpgglpeepfghfai
  window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}

const authClient = new AuthClient(apiAxiosInstance, i18n);
authClient.on("stateChange", (currentUser, previousUser) => {
  // Clear cached query-data if needed.
  if (
    // If a user logs out
    (!currentUser && previousUser) ||
    // Or if a user changes directly (theoretical) or has different permissions
    (currentUser &&
      previousUser &&
      (currentUser.id !== previousUser.id ||
        !isEqual(currentUser.permissions, previousUser.permissions)))
  ) {
    queryClient.clear();
  }
});

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: {
    authClient,
    queryClient,
    i18n,
  },
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultViewTransition: true,
  notFoundMode: "fuzzy",
  defaultNotFoundComponent: () => <FallbackEmpty />,
  defaultErrorComponent: (props) => <FallbackError {...props} />,
});

subscribeAuthRouter(authClient, router);

// Register things for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    /**
     * A state to indicate that Navigation has been executed manually,
     * and Route lifecycle events should opt out of redirecting.
     * Usable for search updates & sub-route navigation(s).
     */
    suppressLifeCycleRedirects?: boolean;
    /**
     * A state to indicate that the user's email was verified successfully.
     */
    emailVerified?: true;
    /**
     * A state to indicate that the user's password was successfully updated.
     */
    passwordUpdated?: true;
  }
}

const rootElement = document.getElementById("app")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    ...sentryRootElementOptions,
  });

  root.render(
    <StrictMode>
      <PostHogProvider
        apiKey={import.meta.env.VITE_POSTHOG_KEY}
        options={createPostHogConfig(authClient)}
      >
        <AuthProvider authClient={authClient}>
          <JotaiProvider>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              <Toaster />
              <BuildUpdateNotice />
            </QueryClientProvider>
          </JotaiProvider>
        </AuthProvider>
      </PostHogProvider>
    </StrictMode>,
  );
}
